import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@cbhq/cds-web/buttons';
import { Box, Grid, VStack } from '@cbhq/cds-web/layout';
import { TextDisplay3, TextTitle4 } from '@cbhq/cds-web/typography';

import { ReactComponent as BaseLogoSvg } from ':cloud/assets/Products/BaseLogo.svg';
import { ReactComponent as LayerThreeSvg } from ':cloud/assets/Products/LayerThree.svg';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, logClick, useLogViewOnMount } from ':cloud/init/clientAnalytics/logging';
import { AppRoute } from ':cloud/utils/routes';
import {
  KeyFeatureItem,
  KeyFeaturesSection,
} from ':cloud/widgets/sharedcomponents/KeyFeaturesSection';

const keyFeatures: KeyFeatureItem[] = [
  {
    iconName: 'lightningNetworkSend',
    iconType: 'pictogram',
    title: 'High speed and throughput',
    description:
      'One second block times, less than 10 second round-trip between L3s and Base, and 5Mgas/s with auto-scaling.',
  },
  {
    iconName: 'findYourSelection',
    iconType: 'pictogram',
    title: 'Fully managed infrastructure',
    description: `You’ll get a Block Explorer, Sequencer, Nodes, and streamlined access to auxiliary infra like bridges and indexers.`,
  },
  {
    iconName: 'dappsL2Support',
    iconType: 'heroSquare',
    title: 'Coinbase users and support',
    description: `Get easy access to Coinbase’s smart wallet, onchain transfers, Onramp, Paymaster, Bundler, OnchainKit, and AgentKit.`,
    icon: <BaseLogoSvg />,
  },
];

export function BaseL3EmptyState() {
  const history = useHistory();
  const { isDesktop } = useSimpleBreakpoints();
  const responsiveTemplateColumnsHeader = useMemo(() => {
    return isDesktop ? '1fr 1fr' : '1fr';
  }, [isDesktop]);

  const handleDeployPress = useCallback(() => {
    history.push(AppRoute.Products.BaseL3Deploy);
    logClick(EventName.base_l3_landing_deploy_click);
  }, [history]);

  useLogViewOnMount(EventName.base_l3_empty_state_view);

  return (
    <Box display="block" width="100%" minWidth="100%" flexGrow={1}>
      <Box display="block" borderedBottom>
        <Grid
          spacing={10}
          width="100%"
          maxWidth={1180}
          templateColumns={responsiveTemplateColumnsHeader}
          gap={6}
        >
          {!isDesktop && (
            <Box display="flex" alignItems="center" justifyContent="center" flexGrow={1}>
              <LayerThreeSvg />
            </Box>
          )}
          <VStack maxWidth={500} gap={1}>
            <TextDisplay3 as="h1">Launch your own L3 chain on Base in minutes</TextDisplay3>
            <TextTitle4 spacingBottom={1} as="h4" color="foregroundMuted">
              Unlock fast and cheap transactions at scale, with dedicated blockspace and seamless
              integration with Base tools.
            </TextTitle4>
            <Box>
              <Button onPress={handleDeployPress}>Deploy an L3</Button>
            </Box>
          </VStack>
          {isDesktop && (
            <Box display="flex" alignItems="center" justifyContent="center" flexGrow={1}>
              <LayerThreeSvg />
            </Box>
          )}
        </Grid>
      </Box>

      <KeyFeaturesSection
        borderedBottom={false}
        sectionTitle="Why Base L3 Launcher?"
        features={keyFeatures}
      />
    </Box>
  );
}
